import axios from 'axios';
import { getAuthHeaders, authService, isDefinitiveAuthError } from './auth';
import logger from './logger';

// Standardize Backend URL (no trailing slash, no /api suffix)
const getEnvUrl = () => {
  // Use REACT_APP_BACKEND_URL as the ONLY allowed source for production readiness
  let url = process.env.REACT_APP_BACKEND_URL || '';
  if (url.endsWith('/')) url = url.slice(0, -1);
  if (url.endsWith('/api')) url = url.slice(0, -4);
  // console.log("API URL:", url);
  return url;
};

export const API_BASE = getEnvUrl();

export const WS_BASE = (process.env.REACT_APP_BACKEND_WS_URL || process.env.REACT_APP_BACKEND_URL || '')
  .replace('https://', 'wss://').replace('http://', 'ws://');

export const DEFAULT_HTTP_TIMEOUT_MS = 15000;

const api = axios.create({
  baseURL: API_BASE,
  timeout: DEFAULT_HTTP_TIMEOUT_MS,
  withCredentials: true,
});

if (process.env.NODE_ENV !== 'production') {
  // console.log("[API] baseURL:", api.defaults.baseURL);
}

api.interceptors.request.use(
  (config) => {
    const token = authService.getToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);


// Response interceptor for global 401 handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && originalRequest) {
      const configUrl = originalRequest.url || '';
      // /auth/logout counts as an auth endpoint: a 401 on logout must never trigger a
      // refresh (that would re-mint the session the user is ending).
      const isAuthEndpoint = configUrl.includes('/auth/login') ||
                             configUrl.includes('/auth/logout') ||
                             configUrl.includes('/enterprise/auth/refresh') ||
                             configUrl.includes('/auth/refresh');
      const isAuthPage = typeof window !== 'undefined' &&
        (window.location.pathname.includes('/auth/login') || window.location.pathname.includes('/auth/signup'));
      const isPublicPage = typeof window !== 'undefined' &&
        (window.location.pathname.includes('/proposal/respond/') ||
        window.location.pathname.includes('/contract/sign/') ||
        window.location.pathname.includes('/onboarding/'));

      // Skip refresh for auth endpoints, public pages, or requests already retried
      if (isAuthEndpoint || isAuthPage || isPublicPage || originalRequest._retry) {
        if (!isAuthEndpoint && !isAuthPage && !isPublicPage) {
          // Check if a newer access token exists in memory that wasn't used in this request
          const currentToken = authService.getToken();
          let requestToken = null;
          if (originalRequest.headers) {
            if (typeof originalRequest.headers.get === 'function') {
              requestToken = originalRequest.headers.get('Authorization') || originalRequest.headers.get('authorization');
            } else {
              requestToken = originalRequest.headers['Authorization'] || originalRequest.headers['authorization'];
            }
          }
          if (requestToken && requestToken.startsWith('Bearer ')) {
            requestToken = requestToken.substring(7);
          }

          if (currentToken && requestToken && currentToken !== requestToken && !originalRequest._staleTokenRetried) {
            originalRequest._staleTokenRetried = true;
            if (originalRequest.headers?.set) {
              originalRequest.headers.set('Authorization', `Bearer ${currentToken}`);
            } else {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers['Authorization'] = `Bearer ${currentToken}`;
            }
            return api(originalRequest);
          }

          if (!authService.hasRefreshToken()) {
            logger.warn("[API] 401 Unauthorized and no refresh token. Logging out...");
            authService.logout();
            if (typeof window !== 'undefined' && !window.location.pathname.includes('/auth/login')) {
              window.location.href = '/auth/login';
            }
          }
        }
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      let newToken;
      try {
        newToken = await authService.refreshToken();
      } catch (refreshErr) {
        // Safe check: verify if another tab has stored a valid unexpired access token
        const latestToken = authService.getToken();
        if (latestToken && !authService.isTokenExpired(latestToken)) {
          newToken = latestToken;
        } else if (isDefinitiveAuthError(refreshErr)) {
          logger.warn("[API] Definitive token refresh failure. Logging out and redirecting...", refreshErr?.message || "Auth error");
          authService.logout();
          if (typeof window !== 'undefined' && !window.location.pathname.includes('/auth/login')) {
            window.location.href = '/auth/login';
          }
          return Promise.reject(refreshErr);
        } else {
          logger.warn("[API] Transient refresh failure, rejecting request without destroying session:", refreshErr?.message || "Transient error");
          return Promise.reject(refreshErr);
        }
      }

      // Refresh succeeded: update Authorization header.
      if (originalRequest.headers?.set) {
        originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
      } else {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
      }

      // Execute retried request outside the refresh try/catch so application errors do not trigger logout
      return api(originalRequest);
    } else if (error.response?.status === 403 && error.response?.data?.detail === "ORG_INACTIVE") {
      // Prevent redirect loop if already on the waiting screen
      if (!window.location.pathname.includes('/auth/waiting-for-activation')) {
        logger.warn("[API] 403 ORG_INACTIVE detected. Redirecting to activation screen...");
        window.location.href = '/auth/waiting-for-activation';
      }
    } else if (
      error.response?.status === 403 &&
      // `error_code` is the machine-readable symbol the backend emits alongside the
      // unchanged legacy body. The `data.detail` forms below are kept for any endpoint
      // that returns a raw FastAPI body without passing through the custom handler.
      (error.response?.data?.error_code === "TRIAL_EXPIRED" ||
        error.response?.data?.detail === "TRIAL_EXPIRED" ||
        error.response?.data?.detail?.detail === "TRIAL_EXPIRED")
    ) {
      logger.warn("[API] 403 TRIAL_EXPIRED intercepted. Triggering modal.");
      import("./trialModalStore").then(({ trialModalStore }) => {
        trialModalStore.open();
      });
      import("sonner").then(({ toast }) => {
        toast.error("Your free trial has expired. Upgrade to continue editing data.", {
          id: "trial-expired-toast",
          duration: 5000
        });
      });
    } else if (
      error.response?.status === 403 &&
      (error.response?.data?.error_code === "SOLUTION_DENIED" ||
        error.response?.data?.detail?.detail === "SOLUTION_DENIED")
    ) {
      // An admin revoked this solution while the tab was open, so the cached session is
      // stale. Tell SessionProvider to re-fetch /api/auth/me; the route guard then
      // redirects out of the now-forbidden section. This self-heals rather than leaving
      // the user on a page whose data will never load.
      const data = error.response.data;
      const solution = data.error_context?.solution ?? data.detail?.solution;
      logger.warn(`[API] 403 SOLUTION_DENIED (${solution}). Refreshing session.`);
      window.dispatchEvent(
        new CustomEvent("synexos:solution-denied", { detail: { solution } })
      );
    }
    return Promise.reject(error);
  }
);




// Dashboard APIs
export const dashboardAPI = {
  getMetrics: () => api.get('/api/dashboard/metrics'),
};

// Customer APIs (Commerce Parties)
export const customerAPI = {
  getAll: () => api.get('/api/commerce/parties/customers'),
  getById: (id) => api.get(`/api/commerce/parties/customers/${id}`),
  getDetails: (id) => api.get(`/api/commerce/parties/customers/${id}/details`),
  create: (data) => api.post('/api/commerce/parties/customers', data),
  update: (id, data) => api.put(`/api/commerce/parties/customers/${id}`, data),
  delete: (id) => api.delete(`/api/commerce/parties/customers/${id}`),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/commerce/parties/customers/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadFile: (formData) => api.post('/api/commerce/parties/customers/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadTemplate: () => api.get('/api/templates/customers', { responseType: 'blob' }),
  exportToExcel: () => api.get('/api/commerce/parties/customers/export/excel', { responseType: 'blob' }),
};

// Vendor APIs (Commerce Parties)
export const vendorAPI = {
  getAll: () => api.get('/api/commerce/parties/vendors'),
  getById: (id) => api.get(`/api/commerce/parties/vendors/${id}`),
  getDetails: (id) => api.get(`/api/commerce/parties/vendors/${id}/details`),
  create: (data) => api.post('/api/commerce/parties/vendors', data),
  update: (id, data) => api.put(`/api/commerce/parties/vendors/${id}`, data),
  delete: (id) => api.delete(`/api/commerce/parties/vendors/${id}`),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/commerce/parties/vendors/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadTemplate: () => api.get('/api/templates/vendors', { responseType: 'blob' }),
};

// Invoice APIs
export const invoiceAPI = {
  getAll: () => api.get('/api/invoices'),
  getById: (id) => api.get(`/api/invoices/${id}`),
  getDetails: (id) => api.get(`/api/invoices/${id}/details`),
  getAging: () => api.get('/api/invoices/aging'),
  create: (data) => api.post('/api/invoices', data),
  update: (id, data) => api.put(`/api/invoices/${id}`, data),
  delete: (id) => api.delete(`/api/invoices/${id}`),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/invoices/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadFile: (formData) => api.post('/api/invoices/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadTemplate: () => api.get('/api/templates/invoices', { responseType: 'blob' }),
  createBill: (data) => api.post('/api/bills', data),
};

// Cash Flow APIs
export const cashFlowAPI = {
  getSummary: (params) => api.get('/api/cashflow/actuals/summary', { params }),
  getTransactions: (params) => api.get('/api/cashflow/actuals/transactions', { params }),
  getStatement: (params) => api.get('/api/cashflow/actuals/statement', { params }),
  getCharts: (params) => api.get('/api/cashflow/actuals/charts', { params }),
  getBudget: () => api.get('/api/cashflow/budget'),
  getForecast: () => api.get('/api/cashflow/forecast'),
  generateAIForecast: () => api.post('/api/cashflow/forecast/generate'),
  getVariance: (period) => api.get(`/api/cashflow/variance?period=${period}`)
};

// Collections APIs
export const collectionsAPI = {
  getAll: () => api.get('/api/collections'),
  updateStatus: (invoiceId, status, comment) =>
    api.post(`/api/collections/${invoiceId}/status`, { status, comment }),
};

// Bill APIs
export const billAPI = {
  getAll: () => api.get('/api/bills'),
  getById: (id) => api.get(`/api/bills/${id}`),
  getDetails: (id) => api.get(`/api/bills/${id}/details`),
  getAging: () => api.get('/api/bills/aging'),
  create: (data) => api.post('/api/bills', data),
  update: (id, data) => api.put(`/api/bills/${id}`, data),
  delete: (id) => api.delete(`/api/bills/${id}`),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/bills/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadTemplate: () => api.get('/api/templates/bills', { responseType: 'blob' }),
};

// Payments APIs
export const paymentsAPI = {
  getAll: () => api.get('/api/payments'),
  updateStatus: (billId, status, comment, scheduledDate = null) =>
    api.post(`/api/payments/${billId}/status`, { status, comment, scheduled_date: scheduledDate }),
};

// Bank Account APIs
export const bankAPI = {
  getAccounts: () => api.get('/api/bank-accounts'),
  createAccount: (data) => api.post('/api/bank-accounts', data),
  updateAccount: (id, data) => api.put(`/api/bank-accounts/${id}`, data),
  deleteAccount: (id) => api.delete(`/api/bank-accounts/${id}`),
  getTransactions: () => api.get('/api/transactions'),
  createTransaction: (data) => api.post('/api/transactions', data),
  deleteTransaction: (id) => api.delete(`/api/transactions/${id}`),
  getMatchSuggestions: (transactionId) => api.get(`/api/transactions/${transactionId}/match-suggestions-enhanced`),
  matchTransaction: (transactionId, entityType, entityId) =>
    api.post(`/api/transactions/${transactionId}/match`, null, {
      params: { entity_type: entityType, entity_id: entityId }
    }),
  matchTransactionManual: (transactionId, matches) =>
    api.post(`/api/transactions/${transactionId}/match-manual`, matches),
  dematchTransaction: (transactionId) => api.post(`/api/transactions/${transactionId}/dematch`),
  getTransactionDetails: (transactionId) => api.get(`/api/transactions/${transactionId}/details`),
  reconcileTransactions: (transactionIds, period) =>
    api.post('/api/transactions/reconcile', { transaction_ids: transactionIds, period }),
  deReconcileTransaction: (transactionId) =>
    api.post(`/api/transactions/${transactionId}/de-reconcile`),
  uploadTransactions: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/transactions/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadTransactionTemplate: () => api.get('/api/templates/transactions', { responseType: 'blob' }),
};


// Category Master APIs
export const categoryAPI = {
  getAllCategories: () => api.get('/api/categories'),
  getAll: (params) => api.get('/api/categories', { params }),
  getById: (id) => api.get(`/api/categories/${id}`),
  getStats: () => api.get('/api/categories/summary/stats'),
  create: (data) => api.post('/api/categories', data),
  getOperatingInflows: () => api.get('/api/categories', {
    params: { cashflow_activity: 'Operating', cashflow_flow: 'Inflow' }
  }),
  getOperatingOutflows: () => api.get('/api/categories', {
    params: { cashflow_activity: 'Operating', cashflow_flow: 'Outflow' }
  }),
};

// Journal Entry APIs
export const journalAPI = {
  getAll: (params) => api.get('/api/journal-entries', { params }),
  getById: (id) => api.get(`/api/journal-entries/${id}`),
  create: (data) => api.post('/api/journal-entries', data),
  delete: (id) => api.delete(`/api/journal-entries/${id}`),
  getInvoiceJournal: (invoiceId) => api.get(`/api/invoices/${invoiceId}/journal`),
  getBillJournal: (billId) => api.get(`/api/bills/${billId}/journal`),
};

// Cash Flow APIs - consolidated with actuals endpoints above

// Reports APIs
export const reportAPI = {
  getARSummary: () => api.get('/api/reports/ar-summary'),
  getAPSummary: () => api.get('/api/reports/ap-summary'),
};

// Financial Reporting APIs
export const financialReportingAPI = {
  getProfitLoss: (params) => api.get('/api/reports/profit-loss', { params }),
  getBalanceSheet: (params) => api.get('/api/reports/balance-sheet', { params }),
  getCashFlowStatement: (params) => api.get('/api/reports/cashflow-statement', { params }),
  getTrialBalance: (params) => api.get('/api/reports/trial-balance', { params }),
  getGeneralLedger: (params) => api.get('/api/reports/general-ledger', { params }),
};


// Deck/Presentation APIs
export const deckAPI = {
  getAll: (isArchived = false) => api.get(`/api/deck?is_archived=${isArchived}`),
  getById: (id) => api.get(`/api/deck/${id}`),
  create: (data) => api.post('/api/deck', data),
  update: (id, data) => api.put(`/api/deck/${id}`, data),
  delete: (id) => api.delete(`/api/deck/${id}`),
  restore: (id) => api.post(`/api/deck/${id}/restore`),
  deletePermanent: (id) => api.delete(`/api/deck/${id}/permanent`),
  
  // Slides
  addSlide: (deckId, data) => api.post(`/api/deck/${deckId}/slides`, data),
  updateSlide: (deckId, slideId, data) => api.put(`/api/deck/${deckId}/slides/${slideId}`, data),
  deleteSlide: (deckId, slideId) => api.delete(`/api/deck/${deckId}/slides/${slideId}`),
  reorderSlides: (deckId, slideIds) => api.post(`/api/deck/${deckId}/slides/reorder`, { slide_ids: slideIds }),
  
  // Elements
  addElement: (deckId, data) => api.post(`/api/deck/${deckId}/elements`, data),
  updateElement: (deckId, elementId, data) => api.put(`/api/deck/${deckId}/elements/${elementId}`, data),
  deleteElement: (deckId, elementId) => api.delete(`/api/deck/${deckId}/elements/${elementId}`),
  
  // Extra Actions
  toggleFavorite: (deckId) => api.post(`/api/deck/${deckId}/favorite`),
  duplicate: (deckId) => api.post(`/api/deck/${deckId}/duplicate`),
  
  // Galleries
  getTemplates: () => api.get('/api/deck/templates'),
  getRecent: () => api.get('/api/deck/recent'),
};

export async function getWsTicket() {
  try {
    const res = await api.post('/api/auth/ws-ticket');
    return res.data?.ticket || null;
  } catch (err) {
    logger.error('[WS Ticket] Failed to obtain ticket:', err?.message || "Unknown error");
    return null;
  }
}

export default api;






